import type { FC } from 'react';
import ChevronDown from '@untitled-ui/icons-react/build/esm/ChevronDown';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@untitled-ui/icons-react/build/esm/ChevronRight';
import SvgIcon from '@mui/material/SvgIcon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { Scrollbar } from 'src/components/scrollbar';
import Edit02Icon from '@untitled-ui/icons-react/build/esm/Edit02';
import { useLocation, useNavigate } from 'react-router';
import { paths } from 'src/paths';
import { Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { nonTherapyPatientPill } from 'src/sections/dashboard/patients/patient-details-listing-table';
import moment from 'moment';
import { multiPayerFlag } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';

type OrderStatus = 'complete' | 'pending' | 'rejected';
const { patients, patientDetails, episodeOfCare, details, editEpisodeOfCare } = paths;

interface Order {
  id: string;
  createdAt: number;
  customer: {
    email: string;
    name: string;
  };
  currency: string;
  items: number;
  number: string;
  status: OrderStatus;
  totalAmount: number;
}

export const EpisodeOfCareTable = ({ episodeOfCareData = [] }: any) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const patientId = state?.patientId;
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const [isMultiPayer] = useFeatureToggleWhenReady(multiPayerFlag, 'FACILITY');

  return (
    <Box>
      <Card>
        <CardHeader
          title={
            <>
              <Typography
                variant="h6"
                display={'inline-block'}
              >
                Episode Of Care
              </Typography>
              <Box
                sx={{
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 0, 0, 0.26)',
                  display: 'inline-block',
                  paddingTop: '3px',
                  paddingLeft: '6px',
                  paddingRight: '6px',
                  paddingBottom: '3px',
                }}
                ml={2}
              >
                <Typography variant="subtitle2">{`${episodeOfCareData?.length} Episode Of Care`}</Typography>
              </Box>
            </>
          }
        />
        <Divider />
        <Scrollbar>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sortDirection="desc">Name</TableCell>
                <TableCell>Residence</TableCell>
                <TableCell>Screening Type</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {episodeOfCareData?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <Typography
                        color={'text.secondary'}
                        variant="subtitle2"
                      >
                        No Records Found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {episodeOfCareData.slice(0, 3).map((data: any) => {
                const startDateFormat = data?.startDate?.split('T')?.at(0);
                const startDateWithoutTimezone = new Date(
                  new Date(startDateFormat).valueOf() +
                    new Date(startDateFormat).getTimezoneOffset() * 60 * 1000
                );

                const endDateFormat = data?.endDate?.split('T')?.at(0);
                const endDateWithoutTimezone = new Date(
                  new Date(endDateFormat).valueOf() +
                    new Date(endDateFormat).getTimezoneOffset() * 60 * 1000
                );

                return (
                  <TableRow
                    hover
                    key={data?.uuid}
                  >
                    <TableCell>
                      <Box
                        display={'flex'}
                        gap={1}
                      >
                        {data?.episodeTitle}
                        {data.isNonTherapyPatient && (
                          <Box sx={nonTherapyPatientPill}>Non-therapy</Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {data?.disciplineOfComplete
                        ?.replace(/_/g, ' ')
                        ?.replace(
                          /\w\S*/g,
                          (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                        )}
                    </TableCell>
                    <TableCell>{data?.screeningType || '-'}</TableCell>
                    <TableCell>
                      {data?.startDate
                        ? moment(startDateWithoutTimezone)?.format('MMM DD, yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {data?.endDate ? moment(endDateWithoutTimezone)?.format('MMM DD, yyyy') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() =>
                          navigate(
                            `${patients}/${patientDetails}${episodeOfCare}${editEpisodeOfCare}`,
                            {
                              state: {
                                episodeOfCareId: data?.uuid,
                                getPatientId,
                                isMultiPayer
                              },
                            }
                          )
                        }
                      >
                        <SvgIcon>
                          <Edit02Icon />
                        </SvgIcon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Scrollbar>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            p: 2,
          }}
        >
          <Button
            color="primary"
            endIcon={
              <SvgIcon>
                <ChevronRightIcon />
              </SvgIcon>
            }
            onClick={(event) =>
              navigate(`${patients}/${patientDetails}${episodeOfCare}`, {
                state: {
                  getPatientId,
                },
              })
            }
            size="small"
          >
            See all Episode of care
          </Button>
        </Box>
      </Card>
    </Box>
  );
};
